import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaArrowLeft, FaPlus, FaDownload, FaUndo, FaRedo,
  FaTrash, FaBold, FaItalic, FaAlignLeft, FaAlignCenter, FaAlignRight,
  FaExpand, FaCompress, FaPlay, FaPause, FaChevronLeft, FaChevronRight
} from 'react-icons/fa';
import { fileSave, supported as fileSystemAccessSupported } from 'browser-fs-access';
import { wishesService } from '../services/wishesService';
import BottomNav from '../components/public/BottomNav';
import TextOverlay from '../components/TextOverlay';
import { renderImageWithText } from '../utils/renderImage';
import { renderVideoWithText } from '../utils/renderVideo';
import { loadGoogleFonts } from '../utils/fonts';
import logo from '../assets/logo.svg';
import './MediaEditor.css';

const MediaEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [mediaData, setMediaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [textLayers, setTextLayers] = useState([]);
  const [selectedLayer, setSelectedLayer] = useState(0); // Always use first layer
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Text editor states
  const [textContent, setTextContent] = useState('');
  const [fontSize, setFontSize] = useState(20);
  const [fontFamily, setFontFamily] = useState('Noto Sans');
  const [textColor, setTextColor] = useState('#000000');
  const [textAlign, setTextAlign] = useState('center');
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [fadeIn, setFadeIn] = useState(0);

  // Flag to prevent auto-update loop when loading layer properties
  const isLoadingLayer = useRef(false);

  // Drag states
  const [isDragging, setIsDragging] = useState(false);
  const [draggingLayerIndex, setDraggingLayerIndex] = useState(null);
  const previewContainerRef = useRef(null);

  // Timeline drag states
  const [isDraggingTimeline, setIsDraggingTimeline] = useState(false);
  const [isDraggingPlayhead, setIsDraggingPlayhead] = useState(false);
  const [resizingLayer, setResizingLayer] = useState(null);
  const [resizeType, setResizeType] = useState(null); // 'start' or 'end'
  const timelineRef = useRef(null);

  // Toast notification states
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingText, setProcessingText] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);

  // iOS Save Modal state (for native-like save to gallery)
  const [iosSaveModal, setIosSaveModal] = useState({ show: false, blobUrl: '', filename: '' });


  // Fullscreen video controls
  const [showFullscreenControls, setShowFullscreenControls] = useState(false);
  const fullscreenControlsTimeoutRef = useRef(null);

  const fontOptions = [
    'Noto Sans', 'Roboto', 'Arial', 'Times New Roman', 'Georgia', 'Verdana',
    'Courier New', 'Comic Sans MS', 'Impact', 'Trebuchet MS', 'Palatino', 'Garamond'
  ];

  // Engaging processing messages
  const processingMessages = [
    'Initializing magic...',
    'Adding your text...',
    'Applying beautiful fonts...',
    'Mixing colors perfectly...',
    'Positioning elements...',
    'Rendering high quality...',
    'Optimizing for sharing...',
    'Almost there...',
    'Finalizing your masterpiece...',
    'Just a moment more...'
  ];

  // Progress simulation
  useEffect(() => {
    let progressInterval;
    let messageInterval;
    let messageIndex = 0;

    if (isProcessing) {
      setProcessingProgress(0);
      setProcessingText(processingMessages[0]);

      // Simulate progress with realistic pacing
      progressInterval = setInterval(() => {
        setProcessingProgress((prev) => {
          if (prev >= 95) return prev; // Cap at 95% until actual completion
          // Slow down as we get closer to 95%
          const increment = prev < 30 ? 8 : prev < 60 ? 5 : prev < 80 ? 3 : 1;
          return Math.min(prev + increment, 95);
        });
      }, 800);

      // Cycle through messages
      messageInterval = setInterval(() => {
        messageIndex = (messageIndex + 1) % processingMessages.length;
        setProcessingText(processingMessages[messageIndex]);
      }, 2500);
    } else {
      // Reset when not processing
      setProcessingProgress(0);
      setProcessingText('');
    }

    return () => {
      clearInterval(progressInterval);
      clearInterval(messageInterval);
    };
  }, [isProcessing]);

  useEffect(() => {
    fetchMediaData();

    // Load Google Fonts immediately
    loadGoogleFonts().then(() => {
      console.log('✅ Google Fonts loaded and ready');
    }).catch(err => {
      console.error('❌ Font load error:', err);
    });
  }, [id]);

  // Set up video duration listener
  useEffect(() => {
    if (mediaData?.mediaType === 'video' && videoRef.current) {
      const video = videoRef.current;
      const handleLoadedMetadata = () => {
        if (videoRef.current) {
          setDuration(videoRef.current.duration);
        }
      };

      video.addEventListener('loadedmetadata', handleLoadedMetadata);

      // If video is already loaded, set duration immediately
      if (video.readyState >= 1) {
        setDuration(video.duration);
      }

      return () => {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      };
    }
  }, [mediaData]);

  // Load layer properties when media loads
  useEffect(() => {
    if (textLayers[0]) {
      isLoadingLayer.current = true;
      const layer = textLayers[0];
      setTextContent(layer.text);
      setFontSize(layer.fontSize);
      setFontFamily(layer.fontFamily);
      setTextColor(layer.color);
      setTextAlign(layer.align);
      setIsBold(layer.bold);
      setIsItalic(layer.italic);
      setFadeIn(layer.fadeIn || 0);
      setTimeout(() => {
        isLoadingLayer.current = false;
      }, 0);
    }
  }, [textLayers.length]);

  // Auto-update single layer in real-time when any property changes
  useEffect(() => {
    // Skip update if we're loading layer properties
    if (isLoadingLayer.current) return;

    if (textLayers[0]) {
      // Update existing layer
      setTextLayers(prev => {
        const updated = [...prev];
        updated[0] = {
          ...updated[0],
          text: textContent,
          fontSize: fontSize,
          fontFamily: fontFamily,
          color: textColor,
          align: textAlign,
          bold: isBold,
          italic: isItalic,
          fadeIn: fadeIn
        };
        return updated;
      });
    } else if (textContent.trim() !== '' && !isLoadingLayer.current) {
      // Auto-create single layer when typing
      const newLayer = {
        id: Date.now(),
        text: textContent,
        fontSize: fontSize,
        fontFamily: fontFamily,
        color: textColor,
        align: textAlign,
        bold: isBold,
        italic: isItalic,
        fadeIn: fadeIn,
        x: 50,
        y: 50,
        startTime: mediaData?.mediaType === 'video' ? currentTime : 0,
        endTime: mediaData?.mediaType === 'video' ? Math.min(currentTime + 5, duration || 5) : 5
      };
      setTextLayers([newLayer]);
    }
  }, [textContent, fontSize, fontFamily, textColor, textAlign, isBold, isItalic, fadeIn]);

  // Simple drag handlers - supporting both mouse and touch
  const startDragging = (e, layerIndex) => {
    e.stopPropagation();
    // Note: preventDefault() removed for touch events to avoid passive listener warnings
    // Touch event default behavior is handled by CSS touch-action: none
    console.log('Start dragging layer', layerIndex);
    setDraggingLayerIndex(layerIndex !== undefined ? layerIndex : 0); // Use passed index or default to 0
    setIsDragging(true);
  };

  const handleMove = (e) => {
    if (!isDragging || draggingLayerIndex === null || !previewContainerRef.current) return;

    // Get coordinates from mouse or touch event
    const clientX = e.type.startsWith('touch') ? e.touches[0].clientX : e.clientX;
    const clientY = e.type.startsWith('touch') ? e.touches[0].clientY : e.clientY;

    const rect = previewContainerRef.current.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;

    const clampedX = Math.max(0, Math.min(100, x));
    const clampedY = Math.max(0, Math.min(100, y));

    setTextLayers(prev => {
      const updated = [...prev];
      updated[draggingLayerIndex] = {
        ...updated[draggingLayerIndex],
        x: clampedX,
        y: clampedY
      };
      return updated;
    });
  };

  const stopDragging = (e) => {
    if (isDragging) {
      console.log('Stop dragging');
      setIsDragging(false);
      setDraggingLayerIndex(null);
    }
  };

  // Handle background click (when not clicking on text)
  const handleBackgroundClick = (e) => {
    // Don't do anything if we're dragging
    if (isDragging) return;

    // For video, toggle play/pause
    if (mediaData?.mediaType === 'video' && videoRef.current) {
      handlePlayPause();
    }
  };


  const fetchMediaData = async () => {
    try {
      setLoading(true);
      const response = await wishesService.getMediaById(id);

      // Helper function to build proper URLs
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const buildUrl = (url) => {
        if (!url) return null;
        // If URL already starts with http/https, return as-is (Cloudinary URLs)
        if (url.startsWith('http://') || url.startsWith('https://')) {
          return url;
        }
        // Otherwise prepend baseURL for local server URLs
        return `${baseURL}${url}`;
      };

      const data = {
        ...response.data,
        mediaUrl: buildUrl(response.data.mediaUrl),
        thumbnailUrl: buildUrl(response.data.thumbnailUrl)
      };

      setMediaData(data);
    } catch (error) {
      console.error('Error fetching media:', error);
      alert('Error loading media. Please try again.');
      navigate('/wishes');
    } finally {
      setLoading(false);
    }
  };

  const handleClearText = () => {
    // Clear the single text layer
    isLoadingLayer.current = true;
    setTextContent('');
    setFontSize(32);
    setFontFamily('Arial');
    setTextColor('#FFFFFF');
    setTextAlign('center');
    setIsBold(false);
    setIsItalic(false);
    setFadeIn(0);
    setTextLayers([]);
    setTimeout(() => {
      isLoadingLayer.current = false;
    }, 0);
  };

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleSeek = (e) => {
    const time = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  // Timeline handlers
  const handleTimelineClick = (e) => {
    if (!timelineRef.current || isDraggingPlayhead || resizingLayer !== null) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const clientX = e.type.startsWith('touch') ? e.touches[0].clientX : e.clientX;
    const x = clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;

    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const startPlayheadDrag = (e) => {
    e.stopPropagation();
    // Note: preventDefault() removed for touch events to avoid passive listener warnings
    // Touch event default behavior is handled by CSS touch-action: none
    setIsDraggingPlayhead(true);
  };

  const handlePlayheadDrag = (e) => {
    if (!isDraggingPlayhead || !timelineRef.current) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const clientX = e.type.startsWith('touch') ? e.touches[0].clientX : e.clientX;
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percentage = x / rect.width;
    const newTime = percentage * duration;

    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const stopPlayheadDrag = () => {
    setIsDraggingPlayhead(false);
  };

  const startResizeLayer = (e, index, type) => {
    e.stopPropagation();
    // Note: preventDefault() removed for touch events to avoid passive listener warnings
    // Touch event default behavior is handled by CSS touch-action: none
    setResizingLayer(index);
    setResizeType(type);
  };

  const handleLayerResize = (e) => {
    if (resizingLayer === null || !timelineRef.current) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const clientX = e.type.startsWith('touch') ? e.touches[0].clientX : e.clientX;
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percentage = x / rect.width;
    const newTime = percentage * duration;

    setTextLayers(prev => {
      const updated = [...prev];
      const layer = { ...updated[resizingLayer] };

      if (resizeType === 'start') {
        layer.startTime = Math.max(0, Math.min(newTime, layer.endTime - 0.5));
      } else if (resizeType === 'end') {
        layer.endTime = Math.max(layer.startTime + 0.5, Math.min(newTime, duration));
      }

      updated[resizingLayer] = layer;
      return updated;
    });
  };

  const stopLayerResize = () => {
    setResizingLayer(null);
    setResizeType(null);
  };

  const handleLayerDragOnTimeline = (e, index) => {
    e.stopPropagation();
    // Note: preventDefault() removed for touch events to avoid passive listener warnings
    // Touch event default behavior is handled by CSS touch-action: none
    setResizingLayer(index);
    setResizeType('move');
    setSelectedLayer(index);
  };

  const handleLayerMove = (e) => {
    if (resizingLayer === null || resizeType !== 'move' || !timelineRef.current) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const clientX = e.type.startsWith('touch') ? e.touches[0].clientX : e.clientX;
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percentage = x / rect.width;
    const newTime = percentage * duration;

    setTextLayers(prev => {
      const updated = [...prev];
      const layer = { ...updated[resizingLayer] };
      const layerDuration = layer.endTime - layer.startTime;

      // Move the layer while maintaining its duration
      layer.startTime = Math.max(0, Math.min(newTime, duration - layerDuration));
      layer.endTime = layer.startTime + layerDuration;

      updated[resizingLayer] = layer;
      return updated;
    });
  };

  useEffect(() => {
    if (resizeType === 'move') {
      window.addEventListener('mousemove', handleLayerMove);
      window.addEventListener('touchmove', handleLayerMove);
      window.addEventListener('mouseup', stopLayerResize);
      window.addEventListener('touchend', stopLayerResize);
      return () => {
        window.removeEventListener('mousemove', handleLayerMove);
        window.removeEventListener('touchmove', handleLayerMove);
        window.removeEventListener('mouseup', stopLayerResize);
        window.removeEventListener('touchend', stopLayerResize);
      };
    } else if (resizingLayer !== null) {
      window.addEventListener('mousemove', handleLayerResize);
      window.addEventListener('touchmove', handleLayerResize);
      window.addEventListener('mouseup', stopLayerResize);
      window.addEventListener('touchend', stopLayerResize);
      return () => {
        window.removeEventListener('mousemove', handleLayerResize);
        window.removeEventListener('touchmove', handleLayerResize);
        window.removeEventListener('mouseup', stopLayerResize);
        window.removeEventListener('touchend', stopLayerResize);
      };
    } else if (isDraggingPlayhead) {
      window.addEventListener('mousemove', handlePlayheadDrag);
      window.addEventListener('touchmove', handlePlayheadDrag);
      window.addEventListener('mouseup', stopPlayheadDrag);
      window.addEventListener('touchend', stopPlayheadDrag);
      return () => {
        window.removeEventListener('mousemove', handlePlayheadDrag);
        window.removeEventListener('touchmove', handlePlayheadDrag);
        window.removeEventListener('mouseup', stopPlayheadDrag);
        window.removeEventListener('touchend', stopPlayheadDrag);
      };
    }
  }, [resizingLayer, resizeType, isDraggingPlayhead, duration]);

  // Handle text dragging with window-level listeners for smooth dragging
  useEffect(() => {
    if (isDragging && draggingLayerIndex !== null) {
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('touchmove', handleMove);
      window.addEventListener('mouseup', stopDragging);
      window.addEventListener('touchend', stopDragging);
      return () => {
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('touchmove', handleMove);
        window.removeEventListener('mouseup', stopDragging);
        window.removeEventListener('touchend', stopDragging);
      };
    }
  }, [isDragging, draggingLayerIndex]);

  const showToast = (message, type = 'info') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: '' });
    }, 3000);
  };

  const toggleFullscreen = () => {
    const videoContainer = previewContainerRef.current;
    if (!videoContainer) return;

    if (!isFullscreen) {
      // Enter fullscreen
      if (videoContainer.requestFullscreen) {
        videoContainer.requestFullscreen();
      } else if (videoContainer.webkitRequestFullscreen) {
        videoContainer.webkitRequestFullscreen();
      } else if (videoContainer.mozRequestFullScreen) {
        videoContainer.mozRequestFullScreen();
      } else if (videoContainer.msRequestFullscreen) {
        videoContainer.msRequestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  // Auto-hide controls in fullscreen
  const resetControlsTimeout = useCallback(() => {
    if (fullscreenControlsTimeoutRef.current) {
      clearTimeout(fullscreenControlsTimeoutRef.current);
    }
    fullscreenControlsTimeoutRef.current = setTimeout(() => {
      setShowFullscreenControls(false);
    }, 3000);
  }, []);

  // Show controls on interaction in fullscreen
  const handleFullscreenInteraction = useCallback(() => {
    if (isFullscreen) {
      setShowFullscreenControls(true);
      resetControlsTimeout();
    }
  }, [isFullscreen, resetControlsTimeout]);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);

      // Show controls when entering fullscreen
      if (isCurrentlyFullscreen) {
        setShowFullscreenControls(true);
        resetControlsTimeout();
      } else {
        setShowFullscreenControls(false);
        if (fullscreenControlsTimeoutRef.current) {
          clearTimeout(fullscreenControlsTimeoutRef.current);
        }
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      if (fullscreenControlsTimeoutRef.current) {
        clearTimeout(fullscreenControlsTimeoutRef.current);
      }
    };
  }, [resetControlsTimeout]);

  // Handle fullscreen timeline click
  const handleFullscreenTimelineSeek = (e) => {
    if (!videoRef.current) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = e.type.startsWith('touch') ? e.touches[0].clientX : e.clientX;
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const newTime = percentage * duration;

    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    handleFullscreenInteraction();
    e.stopPropagation();
  };

  const handleDownload = async () => {
    try {
      if (textLayers.length === 0) {
        showToast('Please add at least one text overlay before downloading.', 'warning');
        return;
      }

      setIsProcessing(true);
      showToast('Processing your media...', 'info');

      if (mediaData.mediaType === 'image') {
        await downloadImageWithText();
        await wishesService.incrementDownloadCount(id);
        setProcessingProgress(100);
        setProcessingText('Complete!');
        await new Promise(resolve => setTimeout(resolve, 500)); // Show 100% briefly
        showToast('✅ Image saved to your device!', 'success');
      } else {
        await downloadVideoWithText();
        await wishesService.incrementDownloadCount(id);
        setProcessingProgress(100);
        setProcessingText('Complete!');
        await new Promise(resolve => setTimeout(resolve, 500)); // Show 100% briefly
        showToast('✅ Video saved successfully!', 'success');
      }
    } catch (error) {
      console.error('Error downloading:', error);
      showToast('❌ Error saving media. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadImageWithText = async () => {
    try {
      console.log('🎨 Download starting...');

      // Use the fresh rendering utility
      const blob = await renderImageWithText(mediaData.mediaUrl, textLayers);
      console.log('✅ Rendering complete');

      // Handle download based on platform
      await handleImageDownload(blob);
    } catch (error) {
      console.error('❌ Download error:', error);
      showToast('❌ Error saving image. Please try again.', 'error');
    }
  };

  const handleImageDownload = async (blob) => {
    return new Promise(async (resolve, reject) => {
      try {
        const filename = `${mediaData.occasionName.replace(/\s+/g, '_')}_${Date.now()}.png`;
        const inStandaloneMode = isPWAStandalone();

        // Try Share API first (PWA mode)
        if (inStandaloneMode && navigator.share && navigator.canShare) {
          try {
            const file = new File([blob], filename, { type: 'image/png' });
            if (navigator.canShare({ files: [file] })) {
              await navigator.share({
                files: [file],
                title: mediaData.occasionName,
                text: `${mediaData.occasionName} Wishes`
              });
              showToast('✅ Image shared successfully!', 'success');
              resolve();
              return;
            }
          } catch (shareErr) {
            if (shareErr.name === 'AbortError') {
              resolve();
              return;
            }
            console.log('Share not available, using download');
          }
        }

        // iOS handling
        if (isIOS()) {
          const url = URL.createObjectURL(blob);
          if (inStandaloneMode) {
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            }, 100);
            showToast('✅ Image saved! Check Photos app', 'success');
          } else {
            const newWindow = window.open(url, '_blank');
            if (newWindow) {
              setTimeout(() => showToast('Long press image and select "Save Image"', 'info'), 1000);
            }
            setTimeout(() => URL.revokeObjectURL(url), 10000);
          }
          resolve();
          return;
        }

        // Android/Desktop - use file save
        try {
          await fileSave(blob, {
            fileName: filename,
            extensions: ['.png'],
            description: 'PNG Image',
            startIn: 'pictures',
            id: 'wishes-images'
          });
          showToast('✅ Image saved!', 'success');
          resolve();
        } catch (err) {
          if (err.name === 'AbortError') {
            showToast('Download cancelled', 'info');
            resolve();
          } else {
            // Fallback download
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            }, 100);
            showToast('✅ Image downloaded!', 'success');
            resolve();
          }
        }
      } catch (error) {
        reject(error);
      }
    });
  };


  // Detect if user is on mobile device
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // Detect if user is on iOS
  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  };

  // Detect if running in PWA standalone mode
  const isPWAStandalone = () => {
    return window.matchMedia('(display-mode: standalone)').matches ||
           window.navigator.standalone === true || // iOS Safari
           document.referrer.includes('android-app://'); // Android TWA
  };

  const downloadVideoWithText = async () => {
    try {
      console.log('🎬 Starting frontend video rendering...');

      // Get video dimensions for rendering
      let videoWidth = videoRef.current ? videoRef.current.videoWidth : 1280;
      let videoHeight = videoRef.current ? videoRef.current.videoHeight : 720;

      // Get preview container dimensions for font scaling
      const previewHeight = previewContainerRef.current
        ? previewContainerRef.current.offsetHeight
        : 400;
      const previewWidth = previewContainerRef.current
        ? previewContainerRef.current.offsetWidth
        : 600;

      // Cap resolution on mobile to prevent browser tab crash from memory pressure
      const mobile = isMobileDevice();
      const MAX_MOBILE_HEIGHT = 720;
      if (mobile && videoHeight > MAX_MOBILE_HEIGHT) {
        const scale = MAX_MOBILE_HEIGHT / videoHeight;
        videoWidth = Math.round(videoWidth * scale);
        videoHeight = MAX_MOBILE_HEIGHT;
        console.log('📱 Mobile: capped resolution to', videoWidth, 'x', videoHeight);
      }

      console.log('Video dimensions:', { width: videoWidth, height: videoHeight });
      console.log('Preview dimensions:', { width: previewWidth, height: previewHeight });

      // Render video with text overlays in the browser
      showToast('🎬 Rendering video...', 'info');
      const result = await renderVideoWithText(
        mediaData.mediaUrl,
        textLayers,
        {
          width: videoWidth,
          height: videoHeight,
          previewWidth: previewWidth,
          previewHeight: previewHeight,
          frameRate: mobile ? 24 : 30,
          quality: mobile ? 'medium' : 'high',
          onProgress: (percent) => setProcessingProgress(percent)
        }
      );

      const { blob, mimeType, isMP4Compatible, conversionFailed } = result;

      console.log('✅ Video rendered successfully');
      console.log('📹 Format:', mimeType, '| MP4 compatible:', isMP4Compatible);

      // Show warning if conversion failed or not MP4 compatible
      if (conversionFailed) {
        showToast('⚠️ MP4 conversion failed. Video saved as WebM - may not play on some mobile devices.', 'warning');
      } else if (!isMP4Compatible) {
        showToast('⚠️ Video format may not play on iPhone/WhatsApp. Use Safari browser for MP4 output.', 'warning');
      }

      // Determine file extension based on MIME type
      const extension = mimeType.includes('mp4') ? 'mp4' : 'webm';
      const filename = `${mediaData.occasionName.replace(/\s+/g, '_')}_wishes.${extension}`;
      const inStandaloneMode = isPWAStandalone();
      const isiOSDevice = isIOS();

      // iOS: Always try Share API first (works in both browser and PWA mode)
      // This provides native "Save to Photos" option in the share sheet
      if (isiOSDevice && navigator.share && navigator.canShare) {
        try {
          const file = new File([blob], filename, { type: mimeType });

          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: mediaData.occasionName,
              text: `${mediaData.occasionName} Wishes`
            });
            showToast('✅ Video shared! Select "Save Video" to save to Photos', 'success');
            return;
          }
        } catch (shareErr) {
          if (shareErr.name === 'AbortError') {
            // User cancelled share
            return;
          }
          console.log('Share API failed, showing save modal:', shareErr);
          // Continue to fallback
        }
      }

      // PWA Standalone Mode (non-iOS) - Use Share API
      if (!isiOSDevice && inStandaloneMode && navigator.share && navigator.canShare) {
        try {
          const file = new File([blob], filename, { type: mimeType });

          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: mediaData.occasionName,
              text: `${mediaData.occasionName} Wishes`
            });
            showToast('✅ Video shared successfully!', 'success');
            return;
          }
        } catch (shareErr) {
          if (shareErr.name === 'AbortError') {
            return;
          }
          console.log('Share API not available, falling back to download:', shareErr);
        }
      }

      // iOS Fallback: Show save modal with video for long-press save
      if (isiOSDevice) {
        const blobUrl = URL.createObjectURL(blob);
        setIosSaveModal({ show: true, blobUrl, filename });
        showToast('📱 Tap and hold the video below, then select "Save to Photos"', 'info');
        return;
      }

      // Android/Desktop - Use direct download
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      }, 100);

      showToast('✅ Video downloaded!', 'success');
    } catch (error) {
      console.error('Error processing video:', error);
      if (error.response?.data?.message) {
        showToast(error.response.data.message, 'error');
      } else {
        showToast('Error processing video. Please try again.', 'error');
      }
      throw error;
    }
  };


  const handleShare = async () => {
    try {
      if (textLayers.length === 0) {
        showToast('Please add at least one text overlay before sharing.', 'warning');
        return;
      }

      setIsProcessing(true);
      showToast('Preparing to share...', 'info');

      // Use the same logic as download - it already handles Share API in PWA mode
      if (mediaData.mediaType === 'image') {
        await downloadImageWithText(); // This already uses Share API in PWA mode
        await wishesService.incrementShareCount(id);
      } else {
        await downloadVideoWithText(); // This already uses Share API in PWA mode
        await wishesService.incrementShareCount(id);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('User cancelled share');
        showToast('Sharing cancelled', 'info');
      } else {
        console.error('Error sharing:', error);
        showToast('❌ Error sharing. Please try again.', 'error');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteAll = () => {
    if (textLayers.length === 0) {
      showToast('No text layers to delete.', 'warning');
      return;
    }

    if (window.confirm('Are you sure you want to delete all text layers?')) {
      setTextLayers([]);
      setSelectedLayer(null);
      showToast('All text layers deleted.', 'success');
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${seconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="editor-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!mediaData) {
    return <div>Media not found</div>;
  }

  return (
    <div className="media-editor">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <span>{toast.message}</span>
        </div>
      )}

      {/* Processing Overlay */}
      {isProcessing && (
        <div className="processing-overlay">
          <div className="processing-container">
            <div className="processing-spinner">
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
            </div>

            <div className="processing-info">
              <h3 className="processing-title">Creating Your Wish</h3>
              <p className="processing-message">{processingText}</p>

              <div className="progress-container">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${processingProgress}%` }}
                  >
                    <div className="progress-shimmer"></div>
                  </div>
                </div>
                <div className="progress-percentage">{processingProgress}%</div>
              </div>

              <p className="processing-tip">✨ Your personalized wish is being crafted with care</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="editor-header ">
        <button className="back-btn absolute" onClick={() => navigate('/wishes')}>
          <FaArrowLeft />
        </button>
        <h2>{mediaData.occasionName}</h2>
        {/* <div className="editor-actions">
          <button className="btn btn-secondary" onClick={handleDownload} disabled={isProcessing}>
            <FaDownload /> Download
          </button>
        </div> */}
      </div>

      <div className="editor-container">
        {/* Main Preview Area */}
        <div className="editor-preview">
          <div
            className="preview-container"
            ref={previewContainerRef}
            style={{ position: 'relative', cursor: isDragging ? 'grabbing' : 'default', touchAction: 'none' }}
            onMouseMove={handleMove}
            onMouseUp={stopDragging}
            onMouseLeave={stopDragging}
            onTouchMove={handleMove}
            onTouchEnd={stopDragging}
            onTouchCancel={stopDragging}
          >
            {mediaData.mediaType === 'image' ? (
              <div className="image-preview" style={{ position: 'relative' }}>
                <img
                  src={mediaData.mediaUrl}
                  alt={mediaData.occasionName}
                  style={{
                    pointerEvents: isDragging ? 'none' : 'auto',
                    userSelect: 'none'
                  }}
                  draggable={false}
                />
                {/* DOM-based text overlay for proper Unicode rendering */}
                <TextOverlay
                  textLayers={textLayers}
                  currentTime={null}
                  isDragging={isDragging}
                  onTextClick={startDragging}
                  onBackgroundClick={handleBackgroundClick}
                />
              </div>
            ) : (
              <div
                className="video-preview"
                style={{ position: 'relative' }}
                onClick={handleFullscreenInteraction}
                onMouseMove={handleFullscreenInteraction}
                onTouchStart={handleFullscreenInteraction}
              >
                {/* Fullscreen Button */}
                <button
                  className="fullscreen-btn"
                  onClick={toggleFullscreen}
                  title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                >
                  {isFullscreen ? <FaCompress /> : <FaExpand />}
                </button>

                {/* Center Play/Pause Button */}
                {!isFullscreen && (
                  <button
                    className="video-center-play-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePlayPause();
                    }}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '70px',
                      height: '70px',
                      borderRadius: '50%',
                      background: 'rgba(0, 0, 0, 0)',
                      backdropFilter: 'blur(10px)',
                      border: '3px solid rgba(255, 255, 255, 0.3)',
                      color: 'white',
                      fontSize: '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      zIndex: 10,
                      opacity: isPlaying ? 0 : 1,
                      pointerEvents: isPlaying ? 'none' : 'auto'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(0, 0, 0, 0.3)';
                      e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(0, 0, 0, 0.3)';
                      e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1)';
                    }}
                  >
                    {isPlaying ? <FaPause /> : <FaPlay style={{ marginLeft: '4px' }} />}
                  </button>
                )}

                {/* Time Display Overlay */}
                <div style={{
                  position: 'absolute',
                  bottom: '10px',
                  left: '10px',
                  background: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: '500',
                  backdropFilter: 'blur(10px)',
                  zIndex: 10
                }}>
                  {formatTime(currentTime)} / {formatTime(duration)}
                </div>

                <video
                  ref={videoRef}
                  src={mediaData.mediaUrl}
                  onTimeUpdate={handleTimeUpdate}
                  style={{
                    pointerEvents: 'none',
                    userSelect: 'none'
                  }}
                  draggable={false}
                />
                {/* DOM-based text overlay for proper Unicode rendering */}
                <TextOverlay
                  textLayers={textLayers}
                  currentTime={currentTime}
                  isDragging={isDragging}
                  onTextClick={startDragging}
                  onBackgroundClick={handleBackgroundClick}
                />

                {/* Fullscreen Video Controls Overlay */}
                {isFullscreen && (
                  <div className={`fullscreen-controls ${showFullscreenControls ? 'show' : 'hide'}`}>
                    {/* Top Controls */}
                    <div className="fullscreen-top-controls">
                      <button
                        className="fullscreen-exit-btn"
                        onClick={toggleFullscreen}
                        title="Exit Fullscreen"
                      >
                        <FaCompress /> Exit Fullscreen
                      </button>
                    </div>

                    {/* Center Play/Pause Button */}
                    <div className="fullscreen-center-controls">
                      <button
                        className="fullscreen-play-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePlayPause();
                          handleFullscreenInteraction();
                        }}
                      >
                        {isPlaying ? <FaPause /> : <FaPlay />}
                      </button>
                    </div>

                    {/* Bottom Controls */}
                    <div className="fullscreen-bottom-controls">
                      <div className="fullscreen-progress">
                        <div className="fullscreen-time-display">
                          {formatTime(currentTime)} / {formatTime(duration)}
                        </div>
                        <div
                          className="fullscreen-timeline"
                          onClick={handleFullscreenTimelineSeek}
                          onTouchStart={handleFullscreenTimelineSeek}
                        >
                          <div
                            className="fullscreen-progress-bar"
                            style={{ width: `${(currentTime / duration) * 100}%` }}
                          />
                          <div
                            className="fullscreen-playhead"
                            style={{ left: `${(currentTime / duration) * 100}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Timeline Editor */}
          {mediaData.mediaType === 'video' && (
            <div className="timeline-section">
              <div
                className="timeline-container"
                ref={timelineRef}
                onClick={handleTimelineClick}
                onTouchStart={handleTimelineClick}
              >
                {/* Time markers */}
                <div className="timeline-markers">
                  {Array.from({ length: Math.ceil(duration) + 1 }, (_, i) => (
                    <div key={i} className="time-marker" style={{ left: `${(i / duration) * 100}%` }}>
                      <span>{formatTime(i)}</span>
                    </div>
                  ))}
                </div>

                {/* Timeline track - Single layer */}
                <div className="timeline-tracks">
                  {textLayers[0] && (() => {
                    const layer = textLayers[0];
                    const startPercent = ((layer.startTime || 0) / duration) * 100;
                    const endPercent = ((layer.endTime || duration) / duration) * 100;
                    const widthPercent = endPercent - startPercent;

                    return (
                      <div
                        key={layer.id}
                        className="timeline-track selected"
                      >
                        <div className="track-label">
                          <span>{layer.text.substring(0, 20) || 'Text Layer'}...</span>
                        </div>
                        <div
                          className="timeline-layer"
                          style={{
                            left: `${startPercent}%`,
                            width: `${widthPercent}%`
                          }}
                          onMouseDown={(e) => handleLayerDragOnTimeline(e, 0)}
                          onTouchStart={(e) => handleLayerDragOnTimeline(e, 0)}
                        >
                          {/* Resize handles */}
                          <div
                            className="resize-handle resize-start"
                            onMouseDown={(e) => startResizeLayer(e, 0, 'start')}
                            onTouchStart={(e) => startResizeLayer(e, 0, 'start')}
                          />
                          <div className="layer-content">
                            {layer.text.substring(0, 20) || 'Text'}...
                          </div>
                          <div
                            className="resize-handle resize-end"
                            onMouseDown={(e) => startResizeLayer(e, 0, 'end')}
                            onTouchStart={(e) => startResizeLayer(e, 0, 'end')}
                          />
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Playhead */}
                <div
                  className="playhead"
                  style={{ left: `${(currentTime / duration) * 100}%` }}
                  onMouseDown={startPlayheadDrag}
                  onTouchStart={startPlayheadDrag}
                >
                  <div className="playhead-line" />
                  <div className="playhead-handle" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Text Editor Panel */}
        <div className="editor-panel">
          {/* Status indicator - only show when text exists */}
          {textLayers.length > 0 && (
            <div>
              <div style={{
                padding: '8px 12px',
                background: 'rgba(59, 130, 246, 0.1)',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#3b82f6',
                fontWeight: '500',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>✏️ Changes apply instantly</span>
                <button
                  onClick={handleClearText}
                  style={{
                    background: 'rgba(239, 68, 68, 0.1)',
                    color: '#ef4444',
                    border: 'none',
                    padding: '4px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
                >
                  <FaTrash size={10} /> Clear
                </button>
              </div>
            </div>
          )}

          <div className="text-input-section">
            <textarea
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              placeholder="TYPE HERE TO CUSTOMISE YOUR WISH"
              rows="3"
            />
          </div>

          <div className="editor-controls">
            {/* Font Family Row */}
            <div className="control-row">
              <div className="control-group-full">
                <label>SELECT FONT</label>
                <select
                  value={fontFamily}
                  onChange={(e) => setFontFamily(e.target.value)}
                  className="font-dropdown"
                >
                  {fontOptions.map(font => (
                    <option key={font} value={font}>{font}</option>
                  ))}
                </select>
              </div>
              <div className="control-group-small">
                <label>FONT SIZE</label>
                <div className="font-size-control">
                  <button
                    onClick={() => setFontSize(Math.max(12, fontSize - 2))}
                    className="nav-btn"
                  >
                    <FaChevronLeft />
                  </button>
                  <span className="size-display">{fontSize}</span>
                  <button
                    onClick={() => setFontSize(Math.min(72, fontSize + 2))}
                    className="nav-btn"
                  >
                    <FaChevronRight />
                  </button>
                </div>
              </div>
            </div>

            {/* Color and Text Style Row */}
            <div className="control-row">
              <div className="control-group-color">
                <label>COLOUR</label>
                <div className="color-picker-wrapper">
                  <input
                    type="color"
                    value={textColor}
                    onChange={(e) => setTextColor(e.target.value)}
                    className="color-input"
                  />
                </div>
              </div>
              <div className="control-group-style">
                <div className='tstyle'>TEXT STYLE</div>
                <div className="style-buttons">
                  <button
                    className={`style-btn ${isItalic ? 'active' : ''}`}
                    onClick={() => setIsItalic(!isItalic)}
                  >
                    <FaItalic />
                  </button>
                  <button
                    className={`style-btn ${isBold ? 'active' : ''}`}
                    onClick={() => setIsBold(!isBold)}
                  >
                    <FaBold />
                  </button>
                </div>
              </div>
            </div>

            {/* Align and Fade In Animation Row */}
            <div className="control-row">
              <div className="control-group-align">
                <label>ALIGN</label>
                <div className="align-buttons">
                  <button
                    className={`align-btn ${textAlign === 'left' ? 'active' : ''}`}
                    onClick={() => setTextAlign('left')}
                  >
                    <FaAlignLeft />
                  </button>
                  <button
                    className={`align-btn ${textAlign === 'center' ? 'active' : ''}`}
                    onClick={() => setTextAlign('center')}
                  >
                    <FaAlignCenter />
                  </button>
                  <button
                    className={`align-btn ${textAlign === 'right' ? 'active' : ''}`}
                    onClick={() => setTextAlign('right')}
                  >
                    <FaAlignRight />
                  </button>
                </div>
              </div>
              <div className="control-group-fade">
                <label>FADE IN ANIMATION</label>
                <div className="slider-wrapper">
                  <input
                    type="range"
                    min="0"
                    max="3"
                    step="0.1"
                    value={fadeIn}
                    onChange={(e) => setFadeIn(parseFloat(e.target.value))}
                    className="fade-slider"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Text Layers List */}
          {/* <div className="text-layers-list">
            <h4>Text Layers ({textLayers.length})</h4>
            {textLayers.map((layer, index) => (
              <div
                key={layer.id}
                className={`layer-item ${selectedLayer === index ? 'active' : ''}`}
                onClick={() => setSelectedLayer(index)}
              >
                <span>{layer.text.substring(0, 20)}...</span>
                <button onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteLayer(index);
                }}>
                  <FaTrash />
                </button>

                
              </div>
            ))}
          </div> */}

          {/* Action Buttons */}
          <div className="panel-action-buttons">
            <button
              className="panel-action-btn delete-btn"
              onClick={handleDeleteAll}
              disabled={isProcessing || textLayers.length === 0}
              title="Delete All"
            >
              <FaTrash />
            </button>
            <button
              className="panel-action-btn download-btn"
              onClick={handleDownload}
              disabled={isProcessing}
              title="Download"
              style={{
                background: textContent.trim() !== '' ? '#244A95' : '',
                color: textContent.trim() !== '' ? 'white' : ''
              }}
            >
              <FaDownload />
              <span>Download</span>
            </button>
          </div>
        </div>
      </div>

      {/* iOS Save Modal - Native-like save to gallery experience */}
      {iosSaveModal.show && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={() => {
            URL.revokeObjectURL(iosSaveModal.blobUrl);
            setIosSaveModal({ show: false, blobUrl: '', filename: '' });
          }}
        >
          <div
            className="bg-white rounded-2xl p-5 max-w-xs w-full text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-800 mb-1">
              Video Ready!
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Save or share your video
            </p>

            <div className="flex flex-col gap-2">
              <button
                onClick={async () => {
                  try {
                    const response = await fetch(iosSaveModal.blobUrl);
                    const blob = await response.blob();
                    const file = new File([blob], iosSaveModal.filename, { type: 'video/mp4' });

                    if (navigator.share && navigator.canShare({ files: [file] })) {
                      await navigator.share({ files: [file], title: 'Video' });
                    }
                  } catch (err) {
                    if (err.name !== 'AbortError') console.log('Share failed:', err);
                  }
                }}
                className="w-full py-3 bg-blue-500 text-white rounded-xl font-medium"
              >
                Share / Save to Photos
              </button>
              <a
                href={iosSaveModal.blobUrl}
                download={iosSaveModal.filename}
                className="w-full py-3 bg-gray-100 text-gray-800 rounded-xl font-medium inline-block"
              >
                Download
              </a>
              <button
                onClick={() => {
                  URL.revokeObjectURL(iosSaveModal.blobUrl);
                  setIosSaveModal({ show: false, blobUrl: '', filename: '' });
                }}
                className="w-full py-2 text-gray-400 text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <BottomNav activeTab="wishes" logo={logo} />
    </div>
  );
};

export default MediaEditor;
